import type { QueryKey } from '@tanstack/react-query';

import { SNIPPET_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { SnippetsFilterParams } from '../../../../shared/snippets';

import { getSnippetsBaseQueryKey } from './shared';

type SnippetsCountResponse = {
  count: number;
};

function getSnippetsCount(userRevisionLocator: string, params: SnippetsFilterParams) {
  return get<SnippetsCountResponse>(
    `/api/revisions/${nuc(userRevisionLocator)}/snippets/count`,
    false,
    params,
  );
}

export const getSnippetsCountQueryKey = (
  userRevisionLocator: string,
  params: SnippetsFilterParams,
): QueryKey => [...getSnippetsBaseQueryKey(userRevisionLocator), 'count', params];

/**
 * Hook to fetch the count of snippets for a given revision locator.
 *
 * @param {string} userRevisionLocator - The locator of the user's revision.
 * @param {SnippetsFilterParams} params - The filter params for the count.
 * @param {boolean} [shouldFetch] - Whether to fetch the count.
 */
export function useSnippetsCount(
  userRevisionLocator: string,
  params: SnippetsFilterParams,
  shouldFetch = true,
) {
  const isSnippetScanningEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  return useQuery<SnippetsCountResponse>({
    queryKey: getSnippetsCountQueryKey(userRevisionLocator, params),
    queryFn: () => getSnippetsCount(userRevisionLocator, params),
    enabled: shouldFetch && isSnippetScanningEnabled && !!userRevisionLocator && !!params.path,
  });
}
